import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardHeader, CardContent, LinearProgress, Alert,
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Select, InputLabel, FormControl, Grid, Typography, Stack,
  Divider, Switch, FormControlLabel
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { couponsApi } from '../../utils/apiClient';
import { useToast } from '../../hooks/useToast';

const emptyForm = { code: '', description: '', type: 'token_bonus', tokenBonus: 0, discountPercent: 0, maxRedemptions: 1, startsAt: '', expiresAt: '', active: true };

const AdminCoupons = () => {
  const toast = useToast();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchCoupons = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await couponsApi.getAll();
      setCoupons(res.data || []);
    } catch (err) {
      setError(`Failed to load coupons: ${err.response?.data?.message || err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCoupons(); }, []);

  const openModal = (coupon) => {
    if (coupon) {
      setEditing(coupon);
      setForm({
        code: coupon.code,
        description: coupon.description || '',
        type: coupon.type,
        tokenBonus: coupon.tokenBonus || 0,
        discountPercent: coupon.discountPercent || 0,
        maxRedemptions: coupon.maxRedemptions || 1,
        startsAt: coupon.startsAt ? coupon.startsAt.split('T')[0] : '',
        expiresAt: coupon.expiresAt ? coupon.expiresAt.split('T')[0] : '',
        active: coupon.active
      });
    } else {
      setEditing(null);
      setForm(emptyForm);
    }
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditing(null); };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.type === 'token_bonus') delete payload.discountPercent; else delete payload.tokenBonus;
      if (editing) {
        await couponsApi.update(editing._id, payload);
      } else {
        await couponsApi.create(payload);
      }
      closeModal();
      fetchCoupons();
    } catch (err) {
      toast.error(`Save failed: ${err.response?.data?.message || err.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (coupon) => {
    if (!window.confirm('Delete coupon?')) return;
    try { await couponsApi.delete(coupon._id); fetchCoupons(); } catch { toast.error('Delete failed'); }
  };

  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0px 16px 45px rgba(15, 23, 42, 0.12)' }}>
      <CardHeader
        title="Coupons"
        subheader="Manage discount and bonus coupons"
        titleTypographyProps={{ variant: 'h5', fontWeight: 700 }}
        action={
          <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={() => openModal(null)} sx={{ borderRadius: 2 }}>
            New Coupon
          </Button>
        }
      />
      {loading && <LinearProgress color="primary" />}
      <CardContent sx={{ pt: 0 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 620 }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' } }}>
                <TableCell>Code</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Redemptions</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coupons.map(c => (
                <TableRow key={c._id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{c.code}</TableCell>
                  <TableCell>
                    <Chip label={c.type === 'token_bonus' ? 'Token Bonus' : '% Discount'} size="small" color={c.type === 'token_bonus' ? 'primary' : 'secondary'} />
                  </TableCell>
                  <TableCell>{c.type === 'token_bonus' ? `${c.tokenBonus} TND` : `${c.discountPercent}%`}</TableCell>
                  <TableCell>{c.redeemedCount}/{c.maxRedemptions}</TableCell>
                  <TableCell>
                    <Chip label={c.active ? 'Active' : 'Inactive'} size="small" color={c.active ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                    {c.expiresAt ? new Date(c.expiresAt).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="primary" onClick={() => openModal(c)} title="Edit">
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(c)} title="Delete">
                      <DeleteOutlineOutlinedIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && coupons.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    <Stack alignItems="center" spacing={1}>
                      <LocalOfferOutlinedIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                      <Typography variant="body2">No coupons found</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>

      <Dialog open={showModal} onClose={closeModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalOfferOutlinedIcon />
          {editing ? 'Edit Coupon' : 'New Coupon'}
        </DialogTitle>
        <Divider />
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2.5}>
              <TextField fullWidth size="small" label="Code" name="code" value={form.code} onChange={handleChange} required disabled={!!editing} />
              <TextField fullWidth size="small" label="Description" name="description" value={form.description} onChange={handleChange} />
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select name="type" value={form.type} label="Type" onChange={handleChange}>
                  <MenuItem value="token_bonus">Token Bonus</MenuItem>
                  <MenuItem value="percentage_discount">% Discount (future)</MenuItem>
                </Select>
              </FormControl>
              {form.type === 'token_bonus' ? (
                <TextField fullWidth size="small" type="number" label="Token Bonus" name="tokenBonus" value={form.tokenBonus} onChange={handleChange} inputProps={{ min: 0 }} />
              ) : (
                <TextField fullWidth size="small" type="number" label="Discount %" name="discountPercent" value={form.discountPercent} onChange={handleChange} inputProps={{ min: 0, max: 100 }} />
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" type="number" label="Max Redemptions" name="maxRedemptions" value={form.maxRedemptions} onChange={handleChange} inputProps={{ min: 1 }} />
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={<Switch name="active" checked={!!form.active} onChange={(e) => setForm(prev => ({ ...prev, active: e.target.checked }))} />}
                    label="Active"
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" type="date" label="Starts At" name="startsAt" value={form.startsAt} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" type="date" label="Expires At" name="expiresAt" value={form.expiresAt} onChange={handleChange} InputLabelProps={{ shrink: true }} />
                </Grid>
              </Grid>
            </Stack>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeModal} variant="outlined" color="inherit">Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Card>
  );
};

export default AdminCoupons;
